<script lang="ts">
	import { afterUpdate, createEventDispatcher, onMount } from 'svelte';
	import { location, push } from 'svelte-spa-router';

	//Helpers
	import { format } from 'svelte-i18n';
	import { deriveRoomName } from '../../../helpers/service_rooms_helpers';
	import {
		setWizardSteps,
		updateWizardStepOnWizardItem,
		updateWizardStepOnPrevNext,
		resetWizardItems,
		filterScenesInitial,
		requestAddDashboardItem,
		requestDeleteDashboardItem,
		scrollToElement,
	} from '../../Lists/list-helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	//Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { user_data, user_settings } from 'src/stores/account/user';
import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';

	import {
		Text,
		Button,
		Modal,
		Wizard,
		WizardItem,
		Container,
		WizardContainer,
		WizardContent,
		Alert,
	} from '@Shelly/shelly-design-system';
	import { NON_PREMIUM_WIDGETS_LIMIT, PREMIUM_WIDGETS_LIMIT } from 'src/constants';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { mobile } from 'src/services/mobile-rework';

	import DashboardWizardSelectList from 'src/components/Dashboards/DashboardWizard/DashboardWizardSelectList.svelte';
	import { extended_dashboards } from 'src/stores/features/dashboards';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	export let editItem;
	export let showAddEditModal;
	export let dashboardId;
	export let items;
	export let updatedItem;

	const isPremium = $user_data.premium || $user_settings.been_premium;
	const dashdId = $location.split('/')[2];
	// Determine if the weather widget card exists on the dashboard
	const hasWeatherWidgetCard = $extended_dashboards[dashdId]?.some((card) => card._type === 'weather');

	// Determine if the weather widget card exists and another card is being edited
	const hasWeatherWidgetCardOnEdit = hasWeatherWidgetCard && editItem.type !== 'weather';

	let showDuplicateItemModal: boolean = false;
	let duplicateItemModalNotification: string = '';
	let selectedType = {
		id: '',
		i18n_sub_name: '',
	};

	// Due to differences in the flow the widgetsCounter is incorrectly counting the 'weather' type twice.
	// The onSelectType function adds 'weather' to allItems, while handleSaveWidgets adds 'weather' to selectedItems.
	// This behavior is necessary because 'weather' needs to be calculated during the first step, whereas the other types are calculated in the second step.
	$: widgetsCounter =
		$edit || selectItems.find((i) => i.type === 'weather')
			? allItems.length
			: allItems.length + selectItems.length;

	$: types = [
		{
			id: 'devices',
			name: capFirstChar(
				$format('tab_devices', {
					default: 'Devices',
				}),
			),
			i18n_sub_name: 'process_device',
			icon: 'fat fa-microchip',
			selected: selectedType.id === 'devices',
			disabled: Object.values($devices).length <= 0,
		},
		{
			id: 'rooms',
			name: capFirstChar(
				$format('tab_rooms', {
					default: 'Rooms',
				}),
			),
			i18n_sub_name: 'process_room',
			icon: 'fat fa-door-open',
			selected: selectedType.id === 'rooms',
			disabled: Object.values($rooms).length <= 0,
		},
		{
			id: 'groups',
			name: capFirstChar(
				$format('tab_groups', {
					default: 'Groups',
				}),
			),
			i18n_sub_name: 'process_group',
			icon: 'fat fa-network-wired',
			selected: selectedType.id === 'groups',
			disabled: Object.values($groups).length <= 0,
		},
		{
			id: 'scenes',
			name: capFirstChar(
				$format('tab_scenes', {
					default: 'Scenes',
				}),
			),
			i18n_sub_name: 'process_scene',
			icon: 'fat fa-film',
			selected: selectedType.id === 'scenes',
			disabled: Object.values($scenes).filter(filterScenesInitial).length <= 0,
		},
		{
			id: 'alarms',
			name: capFirstChar(
				$format('tab_alarms', {
					default: 'Alarms',
				}),
			),
			i18n_sub_name: 'process_alarm',
			icon: 'fat fa-siren-on',
			selected: selectedType.id === 'alarms',
			disabled: Object.values($alarms).length <= 0,
		},
		{
			id: 'temp_schemes',
			name: capFirstChar(
				$format('tab_thermostats', {
					default: 'Thermostats',
				}),
			),
			i18n_sub_name: 'process_thermostat',
			icon: 'fat fa-thermometer-three-quarters',
			selected: selectedType.id === 'temp_schemes',
			disabled: Object.values($temp_schemes).length <= 0,
		},
		{
			id: 'weather',
			name: capFirstChar(
				$format('dashboard_weather_widget_card', {
					default: 'Weather Widget',
				}),
			),
			i18n_sub_name: 'process_weather',
			icon: 'fat fa-light fa-cloud',
			selected: selectedType.id === 'weather',
			badge: $format('label_premium', { default: 'premium' }),
			disabled: !isPremium || ($edit ? hasWeatherWidgetCardOnEdit : hasWeatherWidgetCard),
		},
	];

	$: modalTitle = $edit
		? $format('prompt_title_edit_item', {
				default: `Edit item`,
			})
		: $format('prompt_title_add_item', {
				default: `Add item`,
			});

	const previousButton = $format('button_back');

	let wizardItems = setWizardSteps([
		$format('label_select_type'),
		$format('label_select_items', { default: 'Select items' }),
	]);
	let activeWizardStep = wizardItems[0];
	let selectItems: { type: string; id: number | string }[] = [];
	let selectedItemsForExtermination: { type: string; id: number }[] = [];
	let allTypeRefs: { [key: string | number]: HTMLElement } = {};
	let allItemsRefs: { [key: string | number]: HTMLElement } = {};
	let wizardItemsContainerRef: HTMLElement = null;
	let modalRef: Modal = null;
	let isMounted = false;
	let scrolledToView = false;
	let allItems = [...items];
	let waitingForResponse = false;

	const DASHBOARD_WIDGETS_LIMIT = isPremium ? PREMIUM_WIDGETS_LIMIT : NON_PREMIUM_WIDGETS_LIMIT;

	const modalWidgetLimitReachedTranslation = isPremium
		? 'text_premium_widgets_limit_reached'
		: 'text_basic_widgets_limit_reached';

	const modalWidgetReachedDefault = isPremium
		? `Premium Plan users are limited to ${PREMIUM_WIDGETS_LIMIT} widgets per dashboard. Remove some of your widgets to add a new one.`
		: `Basic Plan users are limited to ${NON_PREMIUM_WIDGETS_LIMIT} widgets per dashboard. Remove some of your widgets to add a new one, or upgrade to Premium to set up to ${PREMIUM_WIDGETS_LIMIT} widgets per dashboard.`;

	const imageUrlWidgetLimitReached = isPremium
		? './images/premium-user-limit-widgets-reached.svg'
		: './images/limit-reached-basic-plan-image.svg';

	const plan = isPremium ? $format('label_premium') : $format('label_basic_plan');

	$: disabledSelectCard = widgetsCounter >= DASHBOARD_WIDGETS_LIMIT;

	onMount(() => {
		if (editItem.type) {
			selectedType = types.find((type) => type.id === editItem.type);
		}

		selectItems = $edit
			? [
					{
						type: editItem.type,
						id: editItem.id,
					},
				]
			: [];

		wizardItems =
			editItem.type === 'weather'
				? setWizardSteps([
						$format('label_select_type'),
						$format('label_select_weather_items', { default: 'Weather info' }),
					])
				: setWizardSteps([
						$format('label_select_type'),
						$format('label_select_items', { default: 'Select items' }),
					]);
	});

	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');

	// Scroll in to view reactive variables
	$: selectedRef = selectItems[0];
	// reset scrolled to view variable after step change
	$: (activeWizardStep.activeIndex, (scrolledToView = false));

	afterUpdate(() => {
		scrollToElementOnMount();
	});

	/**
	 * Determines whether dashboard items can be shown.
	 * - If `itemsCount` reaches the limit and `$edit` is `false`, items cannot be shown.
	 *
	 * @param {number} itemsCount - The current number of dashboard items.
	 * @returns {boolean} `true` if items can be shown, otherwise `false`.
	 */
	const canShowDashboardItems = (itemsCount) => $edit || itemsCount < DASHBOARD_WIDGETS_LIMIT;

	const clearData = () => {
		selectedType = { id: '', i18n_sub_name: '' };
		selectItems = [];
		allItems = [];
		selectedItemsForExtermination = [];
		editItem = {
			roomId: -1,
			position: -1,
			name: '',
			type: '',
			id: -1,
		};
		isMounted = false;

		wizardItems = resetWizardItems(wizardItems);
		activeWizardStep = wizardItems[0];
	};

	let removeContainerEventListener = () => {};

	//#region Wizard Scroll to Functions
	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;
		if (
			activeWizardStep.activeIndex === 1 &&
			selectedRef &&
			allItemsRefs[selectedRef.id] &&
			!scrolledToView
		) {
			wizardStepIndex = 1;
			elementRef = allItemsRefs[selectedRef.id];
		} else if (
			activeWizardStep.activeIndex === 0 &&
			allTypeRefs[selectedType.id] &&
			!scrolledToView
		) {
			wizardStepIndex = 0;
			elementRef = allTypeRefs[selectedType.id];
		}

		if (!elementRef) {
			// Scroll to top, when no card is selected
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const scrollToElementOnMount = () => {
		// Scroll to card on edit when there is selected item on first step
		if (!isMounted && $edit && allTypeRefs[selectedType.id]) {
			isMounted = true;
			allTypeRefs[selectedType.id].scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}
	};
	//#endregion Wizard Scroll to Functions

	const onCloseAddEditModal = () => {
		showAddEditModal = false;
		clearData();
	};

	function handleSaveWidgets() {
		let alreadyExistingItems = [];

		for (const selectItem of selectItems) {
			allItems.find((item) => {
				if (String(item.id) === String(selectItem.id) && item._type === selectItem.type) {
					alreadyExistingItems.push(item);
				}
			});
		}

		if (selectedType.id === 'weather') {
			selectItems = [
				{
					type: 'weather',
					id: 'wc-0',
				},
			];
		}

		if (alreadyExistingItems.length > 0) {
			showDuplicateItemModal = true;
			updatedItem = alreadyExistingItems[alreadyExistingItems.length - 1];

			duplicateItemModalNotification = $edit
				? $format('duplicate_item_modal_notification_on_edit', {
						default: `This widget already exists in the list of the dashboard. If you select it now, it will come to the position of the selected widget.`,
					})
				: $format('duplicate_item_modal_notification_on_add', {
						values: { number: alreadyExistingItems.length },
						default: `${alreadyExistingItems.length} of the selected devices is already in the dashboard. When you add it again it will go to the bottom of the dashboard list.`,
					});
		} else {
			handleAddEditFinish();
		}
	}

	const handleAddEditFinish = async () => {
		try {
			waitingForResponse = true;

			if ($edit) {
				// change one item (of type) to the dash on edit
				await requestDeleteDashboardItem(dashboardId, editItem.type, editItem.id);

				await requestAddDashboardItem(
					dashboardId,
					selectItems[0].type,
					selectItems[0].id,
					editItem.position,
				);

				edit.set(false);
				onCloseAddEditModal();
				dispatch('afterUpdateWidget');
			} else {
				if (selectItems?.[0]?.type !== '') {
					//check if item/s already exist in the current dashboard

					// can add more than one item (of type) to the dash on add
					for (const item of selectItems) {
						await requestAddDashboardItem(dashboardId, item.type, item.id);
					}
					onCloseAddEditModal();
				}
				// can delete more than one item (of type) from the dash
				for (const item of selectedItemsForExtermination) {
					await requestDeleteDashboardItem(dashboardId, item.type, item.id);
				}

				dispatch('afterAddWidget');
			}
		} catch (error) {
			console.error(error);
		}
		waitingForResponse = false;
	};

	const handleSelectItem = (device: object = {}, id: number | string, type: string) => {
		if ($edit) {
			selectItems = [
				{
					type: type,
					id: id,
				},
			];

			updatedItem = {
				type: type,
				id: id,
			};
		} else {
			//if the item is already checked and it is in selectItems than remove it else add it
			if (selectItems.find((item) => item.id === id) !== undefined) {
				selectItems = selectItems.filter((item) => item.id !== id);
			} else {
				selectItems = [
					...selectItems,
					{
						type: type,
						id: id,
					},
				];
			}
		}
	};

	// Wizard Functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onSelectType = (type: string) => {
		if (
			type === 'weather' &&
			!allItems.some((item) => item._type === 'weather') &&
			!hasWeatherWidgetCard &&
			!$edit
		) {
			allItems = [...allItems, { _type: 'weather', id: 'wc-0' }];
		} else if (
			type !== 'weather' &&
			allItems.some((item) => item._type === 'weather') &&
			!hasWeatherWidgetCard
		) {
			allItems = allItems.filter((item) => item._type !== 'weather');
		}

		selectedType = types.find((t) => t.id === type);
		// if type is weather -> rename the second step of the wizard to 'Weather info'
		if (selectedType.id === 'weather') {
			wizardItems = setWizardSteps([
				$format('label_select_type'),
				$format('label_select_weather_items', { default: 'Weather info' }),
			]);
		} else {
			wizardItems = setWizardSteps([
				$format('label_select_type'),
				$format('label_select_items', { default: 'Select items' }),
			]);
		}

		allItemsRefs = {};

		// select edited item
		selectItems = $edit
			? [
					{
						id:
							['rooms', 'groups', 'devices', 'temp_schemes', 'weather'].includes(
								selectItems[0].type,
							) && selectItems[0].type !== selectedType.id
								? 0
								: editItem.id,
						type: editItem.type,
					},
				]
			: [];
	};

	// TODO Move it to helpers and re-use it in GroupsListHolder ?
	const filterDevicesByRoomID: any = (devices: any) => {
		let filteredDevicesByRoom = {};

		devices.forEach((device: any, i) => {
			const roomID = device.room_id;

			const deviceData = {
				name: `${device.name}${!devices_status.getByRealId(device.id)?.online ? ' - Offline' : ''}`,
				id: device.id,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image,
				icon: device.icon,
				backgroundColor: device.backgroundColor,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID] = {
					cards: [...filteredDevicesByRoom[roomID].cards, deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					position: $rooms[roomID]?.position,
				};
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					position: $rooms[roomID]?.position,
				};
			}
		});

		// Remove devices that are not assigned to any room yet.
		// delete filteredDevicesByRoom['-20'];

		return Object.values(filteredDevicesByRoom);
	};

	const closeModal = (e: CustomEvent) => {
		if (showAddEditModal) {
			e.preventDefault();
			showAddEditModal = false;
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});
</script>

<Modal
	on:close={onCloseAddEditModal}
	showModal={showAddEditModal}
	title={canShowDashboardItems(items.length)
		? modalTitle
		: $format('modal_title_widgets_limit_reached', {
				default: 'Widgets Limit Reached',
			})}
	bind:this={modalRef}
	hideSlotToolbar={!canShowDashboardItems(items.length)}
	hideSlotFooter={!canShowDashboardItems(items.length)}
	backdropClose={false}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				disabled={item.disabled || (selectedType.id === '' && !item.active)}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<svelte:fragment slot="content">
		{#if canShowDashboardItems(items.length)}
			<Container padding="scrollPaddings">
				<Alert
					type={!disabledSelectCard ? 'info' : 'warning'}
					heading={$format('alert_heading_widgets_created', {
						values: {
							count: widgetsCounter,
							limit: DASHBOARD_WIDGETS_LIMIT,
						},
						default: `Widgets created: ${widgetsCounter}/${DASHBOARD_WIDGETS_LIMIT}`,
					})}
					message={$format('alert_message_widgets_created', {
						values: {
							plan,
							limit: DASHBOARD_WIDGETS_LIMIT,
						},
						default: `${plan} users are limited to ${DASHBOARD_WIDGETS_LIMIT} widgets per dashboard.`,
					})}
					isHeadingBold
					class="mb-s"
				/>
			</Container>
		{/if}
		<Container padding="0">
			{#if canShowDashboardItems(items.length)}
				<WizardContainer
					activeStepIndex={activeWizardStep.activeIndex}
					adaptiveHeight
					bind:wizardItemsContainerRef
				>
					{#each wizardItems as _, i (i)}
						<!-- DO NOT REMOVE COMMENT: Padding references -->
						<WizardContent
							active={activeWizardStep.activeIndex === i}
							class={classMap({
								'pb-s': true,
								'pt-s': selectedType.id !== 'weather',
							})}
						>
							{#if i === 0}
								<SelectList
									title={$format('label_select_type', {
										default: 'Select type',
									})}
									list={types}
									variant="radio"
									withSearch={false}
									selectCardClass="text-capitalize"
									bind:allCardsRefs={allTypeRefs}
									on:click={(e) => onSelectType(e.detail.id)}
								/>
							{:else if i === 1}
								<DashboardWizardSelectList
									{edit}
									bind:selectedType
									bind:selectItems
									{allItemsRefs}
									{filterDevicesByRoomID}
									bind:widgetsCounter
									on:selectedItem={(e) => {
										handleSelectItem(e.detail.device, e.detail.id, e.detail.type);
									}}
								/>
							{/if}
						</WizardContent>
					{/each}
				</WizardContainer>
			{:else}
				<Alert
					type={'danger'}
					heading={$format('reached_maximum_number_of_widgets', {
						default: 'You have reached the maximum number of widgets',
					})}
					isHeadingBold
					class="mb-s"
				/>
				<div class="d-flex justify-content-center mt-l mb-m">
					<img src={imageUrlWidgetLimitReached} alt="home-screen-mobile-device" />
				</div>

				<Text class="text-center mb-s" element="h1">
					{$format('text_title_widgets_limit_reached', {
						values: { limit: DASHBOARD_WIDGETS_LIMIT },
						default: `You have reached your limit of ${DASHBOARD_WIDGETS_LIMIT} widgets!`,
					})}
				</Text>
				<Text class="text-center text-color-muted" styleType="body-extra-small">
					{$format(modalWidgetLimitReachedTranslation, {
						values: {
							limit: DASHBOARD_WIDGETS_LIMIT,
							premiumLimit: PREMIUM_WIDGETS_LIMIT,
						},
						default: modalWidgetReachedDefault,
					})}
				</Text>
				{#if !isPremium}
					<div class="d-flex justify-content-center">
						<Button
							class="mt-m mb-m text-capitalize"
							variant="premium"
							size="l"
							width="auto"
							on:click={() => {
								setSidePanelState({ open: false });
								push('/settings/premium');
							}}
						>
							{capFirstChar($format('button_get_premium', { default: 'get premium' }))}
						</Button>
					</div>
					<Text
						styleType="body"
						class="text-center link-styles"
						on:click={onCloseAddEditModal}
					>
						{capFirstChar(
							$format('button_maybe_later', {
								default: 'maybe later',
							}),
						)}
					</Text>
				{/if}
			{/if}
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeWizardStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={waitingForResponse ||
				selectedType.id === '' ||
				(!$edit &&
					isLastStepWizard &&
					selectItems.length <= 0 &&
					selectedType.id !== 'weather') ||
				($edit &&
					isLastStepWizard &&
					selectedType.id !== 'weather' &&
					(selectedType.id !== 'temp_schemes'
						? selectItems[0].type !== selectedType.id
						: !['temp_schemes'].includes(selectItems[0].type)))}
			on:click={() => {
				if (isLastStepWizard) {
					handleSaveWidgets();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<Modal
	on:close={() => {
		showDuplicateItemModal = false;
	}}
	showModal={showDuplicateItemModal}
	title={$format('modal_duplicate_title', { default: 'Duplicate widgets' })}
>
	<Container slot="content" padding="0">
		<Text>
			{$format('on_duplicate_item_card_msg', {
				values: { card_name: editItem.name },
				default: duplicateItemModalNotification,
			})}
		</Text>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				showDuplicateItemModal = false;
			}}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				handleAddEditFinish();
			}}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
